/* eslint-disable no-empty */
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import AmbraContainer from '../components/ambraContainer/AmbraContainer';
import VideoWrapper from '../components/common/VideoWrapper';
import PdfViewer from '../components/pdfViewer/PdfViewer';
import PremiumSubscriptionDialog from '../components/selfHelpMaterials/PremiumSubscriptionDialog';
import Resource, { DOCS, VIDEO, DCM } from '../models/Resource';

const DEFAULT_CRITERIA = {
  category: 'self-help',
  limit: 10,
  page: 0,
  type: null,
};

export const DEFAULT_VIDEO_CRITERIA = { ...DEFAULT_CRITERIA, type: VIDEO };
export const DEFAULT_DOCS_CRITERIA = { ...DEFAULT_CRITERIA, type: DOCS };
export const DEFAULT_STUDIES_CRITERIA = { ...DEFAULT_CRITERIA, type: DCM };
export default class SelfHelpMaterialsBloc {
  videos$ = new BehaviorSubject([]);
  videosCount$ = new BehaviorSubject(0);
  isRequestingVideos$ = new BehaviorSubject(false);
  videoCriteria$ = new BehaviorSubject(DEFAULT_VIDEO_CRITERIA);

  documents$ = new BehaviorSubject([]);
  documentsCount$ = new BehaviorSubject(0);
  isRequestingDocuments$ = new BehaviorSubject(false);
  documentCriteria$ = new BehaviorSubject(DEFAULT_DOCS_CRITERIA);

  studies$ = new BehaviorSubject([]);
  studiesCount$ = new BehaviorSubject(0);
  isRequestingStudies$ = new BehaviorSubject(false);
  studyCriteria$ = new BehaviorSubject(DEFAULT_STUDIES_CRITERIA);
  runJoyride$ = new BehaviorSubject(false);

  selfHelpCategories$ = new BehaviorSubject([]);

  constructor({ materialRepository, errorRepository, dialogRepository, userRepository, resourceRepository, selfHelpCategoryRepository }) {
    this.materialRepository = materialRepository;
    this.errorRepository = errorRepository;
    this.dialogRepository = dialogRepository;
    this.userRepository = userRepository;
    this.resourceRepository = resourceRepository;
    this.selfHelpCategoryRepository = selfHelpCategoryRepository;

    this.handleInitData();
    this.checkShouldRunJoyride();
  }

  get availableReport$() {
    return this.userRepository.userDetails$.pipe(
      map((details) => {
        return details.availableReport || null;
      })
    );
  }

  handleInitData() {
    this.videoCriteria$.subscribe((criteria) => this.getVideos(criteria));
    this.documentCriteria$.subscribe((criteria) => this.getDocuments(criteria));
    this.studyCriteria$.subscribe((criteria) => this.getStudies(criteria));
    this.getSelfHelpCategories();
  }

  updateVideoCriteria(updatedCriteria = {}) {
    this.videoCriteria$.next({
      ...this.videoCriteria$.getValue(),
      ...updatedCriteria,
    });
  }

  updateDocumentCriteria(updatedCriteria = {}) {
    this.documentCriteria$.next({
      ...this.documentCriteria$.getValue(),
      ...updatedCriteria,
    });
  }

  updateStudyCriteria(updatedCriteria = {}) {
    this.studyCriteria$.next({
      ...this.studyCriteria$.getValue(),
      ...updatedCriteria,
    });
  }

  async getVideos(criteria) {
    const materials = await this.materialRepository.getMaterials(criteria);
    this.videos$.next(materials?.data?.map((material) => new Resource(material)));
    this.videosCount$.next(materials?.total);
  }

  async getDocuments(criteria) {
    const materials = await this.materialRepository.getMaterials(criteria);
    this.documents$.next(materials?.data?.map((material) => new Resource(material)));
    this.documentsCount$.next(materials?.total);
  }

  async getStudies(criteria) {
    const materials = await this.materialRepository.getMaterials(criteria);
    this.studies$.next(materials?.data?.map((material) => new Resource(material)));
    this.studiesCount$.next(materials?.total);
  }

  async openViewer(material) {
    if (material.type === DCM) {
      try {
        // Should reload the resource info because the viewerLink may have expired
        const resourceInfo = await this.getResourceByUuid(material.resource.uuid);
        this.dialogRepository.loadDialog(AmbraContainer, {
          onClose: () => {
            this.dialogRepository.toggleDialog(false);
          },
          url: resourceInfo?.viewerLink,
        });
        // eslint-disable-next-line no-empty
      } catch (error) {
        this.errorRepository.error$.next({
          message: error?.message || 'Failed to get resource info!',
          severity: 'error',
        });
      }
    }

    if (material.type === VIDEO) {
      const videoUrl = material?.h264Path || material.path;
      this.dialogRepository.loadDialog(VideoWrapper, { url: videoUrl });
    }

    if (material.type === DOCS) {
      this.dialogRepository.loadDialog(PdfViewer, { title: material?.title || '', url: material?.resource?.path });
    }
  }

  getCurrentUser() {
    return this.userRepository.getCurrentUser();
  }

  openSubscribeDialog() {
    this.dialogRepository.loadDialog(PremiumSubscriptionDialog, {
      availableReport$: this.availableReport$,
      onCloseBtnAction: () => this.dialogRepository.toggleDialog(false),
    });
  }

  getResourceByUuid(uuid) {
    return this.resourceRepository.getResourceInfo(uuid);
  }

  checkShouldRunJoyride() {
    const shouldRun = this.userRepository.getShouldRunSelfHelpJoyride();
    if (!shouldRun) return;

    this.userRepository.removeShouldRunSelfHelpJoyride();
    this.runJoyride$.next(true);
  }

  closeJoyride() {
    this.runJoyride$.next(false);
  }

  async getSelfHelpCategories() {
    try {
      const selfHelpCategories = await this.selfHelpCategoryRepository.getCategories();
      this.selfHelpCategories$.next(selfHelpCategories);
    } catch (error) {}
  }
}
